import { FC, useCallback, useEffect } from "react";
import { Stack, Tooltip } from "@mui/material";
import { useEffectOnceWhen } from "rooks";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import type { CustomHeaderProps } from "ag-grid-react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { resourceGridListingPriceFrequencySelector } from "@/store/resources-grid/selectors/grid/resourceGridListingPriceFrequencySelector";
import { setGridResourceListingPriceFrequency } from "@/store/resources-grid/resourcesGridSlice";
import { listingPriceColumnDefField } from "@/components/pages/common/resources-ag-grid/components/column-defs/common/listingPriceColumnDef";
import { TypographyWithTooltip } from "@/components/common/TypographyWithTooltip";
import { ResourceListingPriceDateSelect } from "@/components/pages/common/resources-ag-grid/components/column-defs/column-def-cell-renderers/listing-price/ResourceListingPriceDateSelect";

export const ResourceListingPriceHeaderComponent: FC<CustomHeaderProps> = ({
  displayName,
  innerHeaderComponentParams,
  api,
  ...props
}) => {
  const dispatch = useAppDispatch();
  const tooltip = props.column.getColDef().headerTooltip;

  const { resourceType } = innerHeaderComponentParams;
  const masterGridId = props.context?.masterGridId;

  const frequency = useAppSelector((state) =>
    resourceGridListingPriceFrequencySelector(
      state,
      resourceType,
      masterGridId,
    ),
  );

  const dispatcher = useCallback(
    (frequency: string) => {
      if (!masterGridId) {
        return;
      }

      dispatch(
        setGridResourceListingPriceFrequency({
          frequency,
          resourceType,
          gridId: masterGridId,
        }),
      );
    },
    [dispatch, masterGridId, resourceType],
  );

  useEffectOnceWhen(() => {
    dispatcher("monthly");
  }, !frequency);

  useEffect(() => {
    props.column.getColDef().context = {
      [listingPriceFrequencyKeyForLocalContext]: frequency,
    };

    const defaultSortModel = api.getState()?.sort?.sortModel?.at(0);
    const defaultSort = defaultSortModel?.sort ?? "asc";
    const defaultSortColId = defaultSortModel?.colId;
    const sortedByListingPrice =
      defaultSortColId === listingPriceColumnDefField;

    if (sortedByListingPrice) {
      api.applyColumnState({
        state: [{ colId: listingPriceColumnDefField, sort: null }],
        defaultState: { sort: null },
      });

      setTimeout(() => {
        api.applyColumnState({
          state: [{ colId: listingPriceColumnDefField, sort: defaultSort }],
          defaultState: { sort: null },
        });
      });
    }
  }, [api, props.column, frequency]);

  if (!frequency) {
    return null;
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <TypographyWithTooltip
        title={displayName}
        variant="inherit"
        fontWeight="inherit"
      />

      <ResourceListingPriceDateSelect value={frequency} onChange={dispatcher} />

      {tooltip && (
        <Tooltip arrow title={tooltip} placement="top">
          <HelpOutlineIcon
            fontSize="small"
            color="info"
            sx={{ verticalAlign: "middle", ml: 1 }}
          />
        </Tooltip>
      )}
    </Stack>
  );
};

export const listingPriceFrequencyKeyForLocalContext =
  "listingPriceFrequencyKeyForLocalContext";
